<template>
  <div>
    <b-modal id="contact-lens-prescription-modal" size="lg" centered @show="fetchPrescriptions">
      <template v-slot:modal-header="{ close }">
        <b-container>
          <div class="modal-title">
            <h5 class="cl-modal-title">Contact Lens Prescription</h5>
            <div class="cl-modal-subtitle">
              <span class="text-muted">{{ invoice }}</span>
            </div>
            <button type="button" aria-label="Close" class="close" @click="close">
              <i class="di-remove-10"></i>
            </button>
          </div>
        </b-container>
      </template>

      <div class="cl-modal-body">
        <div v-if="loading" class="text-center py-4">
          <b-spinner small />
        </div>
        <div v-else-if="error" class="text-center py-4 text-muted">
          <p>Unable to load contact lens prescriptions</p>
          <b-button variant="outline-secondary" size="sm" @click="fetchPrescriptions">Retry</b-button>
        </div>
        <div v-else-if="!prescriptions.length" class="text-center py-4 text-muted">
          This customer has no contact lens prescriptions yet.
        </div>
        <template v-else>
          <p class="cl-modal-hint text-muted">
            Select which existing prescription this order should use. Prescription values are not
            changed here.
          </p>
          <div
            v-for="prescription in prescriptions"
            :key="prescription.id"
            class="cl-prescription"
            :class="{ selected: selectedId === prescription.id }"
            @click="selectedId = prescription.id"
          >
            <span class="cl-prescription__radio" />
            <div class="cl-prescription__content">
              <div class="cl-prescription__heading">
                <span class="font-weight-medium">
                  {{ prescription.created_at | moment('timezone', timezone, 'D MMM YYYY') }}
                </span>
                <small class="text-muted ml-2">#{{ prescription.id }}</small>
                <b-badge v-if="prescription.is_expired" variant="warning" class="ml-2">
                  Expired
                </b-badge>
                <b-badge v-if="prescription.is_selected" variant="secondary" class="ml-2">
                  Currently used
                </b-badge>
              </div>
              <b-row class="mt-2">
                <b-col cols="6">
                  <div class="lenses-to-pick-box">
                    <p class="font-weight-bold mb-1">Right</p>
                    <div v-for="(value, label) in prescription.display.prescriptionR" :key="label">
                      <b>{{ label }}:</b> {{ value }}
                    </div>
                  </div>
                </b-col>
                <b-col cols="6">
                  <div class="lenses-to-pick-box">
                    <p class="font-weight-bold mb-1">Left</p>
                    <div v-for="(value, label) in prescription.display.prescriptionL" :key="label">
                      <b>{{ label }}:</b> {{ value }}
                    </div>
                  </div>
                </b-col>
              </b-row>
            </div>
          </div>
        </template>
      </div>

      <template v-slot:modal-footer>
        <b-button variant="outline-secondary" class="mr-auto" @click="openNewPrescription">
          New Contact Lens Prescription
        </b-button>
        <span v-if="saveError" class="text-danger mr-3">{{ saveError }}</span>
        <b-button
          variant="outline-secondary"
          @click="$bvModal.hide('contact-lens-prescription-modal')"
        >
          Cancel
        </b-button>
        <b-button variant="primary" :disabled="!canSave" @click="save">
          <b-spinner v-if="saving" small />
          <span v-else>Save</span>
        </b-button>
      </template>
    </b-modal>

    <NewPrescriptionModal
      :customer="customer"
      :defaultTypeId="CONTACT_LENS_TYPE_ID"
      modalId="contact-lens-new-prescription-modal"
    />
  </div>
</template>

<script>
import apiOrders from '@/api/orders';
import NewPrescriptionModal from '@/components/NewPrescriptionModal.vue';

const CONTACT_LENS_TYPE_ID = 2;

export default {
  name: 'ContactLensPrescriptionModal',
  components: {
    NewPrescriptionModal,
  },
  data() {
    return {
      CONTACT_LENS_TYPE_ID,
      loading: false,
      saving: false,
      error: false,
      saveError: '',
      prescriptions: [],
      selectedId: null,
      currentId: null,
      createdId: null,
      isManuallySelected: false,
      orderId: null,
      itemIds: [],
      invoice: '',
      customer: null,
      timezone: 'Australia/Sydney',
    };
  },
  computed: {
    canSave() {
      if (this.saving || !this.selectedId) return false;
      // Nothing pinned yet: saving pins whatever the order is resolving to today, so it stops
      // drifting to a newer prescription later.
      if (!this.isManuallySelected) return true;
      return this.selectedId !== this.currentId;
    },
  },
  mounted() {
    this.$root.$on('openContactLensPrescription', this.open);
    this.$root.$on('prescriptionListMutated', this.onPrescriptionListMutated);
  },
  beforeDestroy() {
    this.$root.$off('openContactLensPrescription', this.open);
    this.$root.$off('prescriptionListMutated', this.onPrescriptionListMutated);
  },
  methods: {
    open({ orderId, itemIds, invoice, customer, timezone }) {
      this.orderId = orderId;
      this.itemIds = itemIds;
      this.invoice = invoice;
      this.customer = customer;
      this.timezone = timezone;
      this.prescriptions = [];
      this.selectedId = null;
      this.currentId = null;
      this.createdId = null;
      this.isManuallySelected = false;
      this.error = false;
      this.saveError = '';

      this.$bvModal.show('contact-lens-prescription-modal');
    },
    openNewPrescription() {
      this.$bvModal.show('contact-lens-new-prescription-modal');
    },
    // Fired by NewPrescriptionModal once it saves. Preselect what was just created so staff
    // only have to press Save.
    onPrescriptionListMutated() {
      if (!this.orderId) return;

      const knownIds = this.prescriptions.map(prescription => prescription.id);

      this.fetchPrescriptions().then(() => {
        const created = this.prescriptions.find(
          prescription => !knownIds.includes(prescription.id),
        );
        if (created) {
          this.createdId = created.id;
        }
        if (this.createdId) {
          this.selectedId = this.createdId;
        }
      });
    },
    fetchPrescriptions() {
      this.loading = true;
      this.error = false;

      return apiOrders
        .getContactLensPrescriptions(this.orderId)
        .then(({ data }) => {
          this.prescriptions = data.data || [];
          this.currentId = this._.get(data, 'meta.selected_prescription_id') || null;
          this.isManuallySelected = !!this._.get(data, 'meta.is_manually_selected');
          this.selectedId = this.currentId;
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      this.saving = true;
      this.saveError = '';

      apiOrders
        .updateContactLensPrescription(this.orderId, this.selectedId)
        .then(() => {
          this.$store.commit('SET_EXPAND_ITEM_ID', this.itemIds);
          this.$root.$emit('reloadExpandedJob', this.orderId);
          this.$bvModal.hide('contact-lens-prescription-modal');
        })
        .catch(error => {
          this.saveError =
            this._.get(error, 'response.data.message') || 'Could not save the prescription.';
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.cl-modal-title {
  text-align: center;
  font-size: 22px;
  font-weight: 500;
  color: #2a2a2a;
  margin-bottom: 6px;
}

.cl-modal-subtitle {
  text-align: center;
  font-size: 14px;
}

.cl-modal-body {
  padding: 0 20px 10px;
}

.cl-modal-hint {
  font-size: 13px;
  margin-bottom: 16px;
}

.cl-prescription {
  display: flex;
  align-items: flex-start;
  border: 1px solid #ebeef4;
  border-radius: 6px;
  background-color: #fff;
  padding: 16px 20px;
  margin-bottom: 12px;
  cursor: pointer;
}

.cl-prescription:hover {
  border-color: #c9d2e0;
}

.cl-prescription.selected {
  border-color: #2b82fc;
  background-color: #f7faff;
}

.cl-prescription__radio {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin-right: 16px;
  margin-top: 1px;
  border: 1px solid #adb5bd;
  border-radius: 50%;
  background-color: #fff;
}

.cl-prescription.selected .cl-prescription__radio {
  border: 6px solid #2b82fc;
}

.cl-prescription__content {
  flex: 1 1 auto;
  min-width: 0;
}

.cl-prescription__heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
</style>
